import {
  getPackVariant,
  getProductSelection,
  selectOption,
  selectPlan,
} from '@/product/product-selection';
import {
  getPackCompareAtPrice,
  getPackSavingsPercent,
} from '@/product/product-selection/get-pack-savings-percent';
import {
  getPackOptionName,
  isSizeOption,
  SizeOptionName,
} from '@/product/product-selection/pack-size';
import plural from '@/utilities/plural';
import {batch} from '@preact/signals';
import {useState} from 'preact/hooks';
import DeliverySelectorLabel from './DeliverySelectorLabel';
import PackSelectorButton from './PackSelectorButton';
import SelectorItemRadioBox from './SelectorItemRadioBox';

const saveLabel = 'Save';
const badges = [
  {
    packSize: 3,
    badge: 'Most Popular',
    badgeColor: 'sunshine',
  },
  {
    packSize: 12,
    badge: 'Best Value',
    badgeColor: 'deep-ocean-blue',
  },
  {
    packSize: 180,
    badge: 'Most Popular',
    badgeColor: 'sunshine',
  },
  {
    packSize: 720,
    badge: 'Best Value',
    badgeColor: 'deep-ocean-blue',
  },
];

export type PackSelectorProps = {
  currency?: string;
  locale?: string;
  formatMoneyWithDecimals?: boolean;
  onetimePurchaseLabel?: string;
};

export default function PackSelector({
  currency = 'USD',
  locale,
  formatMoneyWithDecimals,
  onetimePurchaseLabel = 'One-Time Purchase',
}: PackSelectorProps) {
  const {onetimePlan, packOptions, product, selectedPlan, selectedVariant} =
    getProductSelection();

  const [selectedPackSize, setSelectedPackSize] = useState<string | null>(
    packOptions[0] ?? null
  );

  // Get color settings for the selected variant
  const selectedColor = selectedVariant?.accentColor ?? '';

  const price = getPackVariant(selectedPackSize ?? '1 Pack')?.price ?? 0;

  const compareAtPrice = product
    ? getPackCompareAtPrice(selectedPackSize ?? '1', product)
    : 0;

  const isSelected = selectedPlan === onetimePlan;

  /**
   * Handles the click event for the one-time purchase button.
   * If the onetimePlan is defined, it selects the plan.
   * If the selectedPackSize is defined, it selects the pack size option.
   */
  const handleClickOnetimePurchase = () => {
    batch(() => {
      selectPlan(onetimePlan);
      if (selectedPackSize) {
        const packOptionName = product
          ? getPackOptionName(product)
          : SizeOptionName.PackSize;
        selectOption({
          name: packOptionName,
          value: selectedPackSize,
        });
      }
    });
  };

  /**
   * Handles the click event for selecting a pack size.
   *
   * @param value - The selected pack size value.
   */
  const handleClickPackSize = (value: string) => {
    setSelectedPackSize(value);
    const packOptionName = product
      ? getPackOptionName(product)
      : SizeOptionName.PackSize;
    selectOption({name: packOptionName, value});
  };

  return product && selectedVariant && packOptions.length > 0 ? (
    <SelectorItemRadioBox
      color={selectedColor}
      dataTestid="one-time-purchase"
      isSelected={isSelected}
      onClick={handleClickOnetimePurchase}
      label={
        <DeliverySelectorLabel
          compareAtPrice={compareAtPrice}
          price={price}
          currency={currency}
          locale={locale}
          formatMoneyWithDecimals={formatMoneyWithDecimals}
        >
          {onetimePurchaseLabel}
        </DeliverySelectorLabel>
      }
    >
      <div className="grid grid-cols-4 gap-x-2 gap-y-5 pt-5">
        {packOptions.map((option) => {
          const packOption = selectedVariant.selectedOptions.find((option) =>
            isSizeOption(option, product.sizeOptionNames)
          );
          const isSelected = packOption?.value === option;

          const savings = Math.round(
            getPackSavingsPercent(option, product) * 100
          );
          const savingsLabel = `${saveLabel} ${savings}%`;

          const badge = badges.find(
            (badge) => badge.packSize === parseInt(option)
          );
          const packSize = parseInt(option, 10);

          const packLabel = plural(
            packSize,
            option.split(' ').find((word) => word !== packSize.toString()) ??
              'PACK'
          );

          return (
            <PackSelectorButton
              badge={badge?.badge}
              badgeColor={badge?.badgeColor}
              bottom={plural(packSize, packLabel)}
              color={selectedColor}
              dataTestId={`pack-size-${packSize}`}
              isSelected={isSelected}
              key={option}
              middle={packSize}
              onClick={() => handleClickPackSize(option)}
              top={savingsLabel}
            />
          );
        })}
      </div>
    </SelectorItemRadioBox>
  ) : (
    <></>
  );
}
